import React from "react";
import { PromptConfig } from "../../models";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from "@mui/material";

interface PersonaPromptConfigFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptConfig: PromptConfig;
  onPromptConfigChange: (promptConfig: PromptConfig) => void;
}

export const PersonaPromptConfigFormDialog: React.FC<PersonaPromptConfigFormDialogProps> = ({ open, onOpenChange, promptConfig, onPromptConfigChange }) => {
  function handlePromptConfigChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) {
    const field = event.target.name;

    if (["autoMoveTime", "showNextTime", "inactiveTime", "notSelectingTime", "failTime"].includes(field)) {
      if (parseInt(event.target.value as string) < 0) {
        event.target.value = 0;
      }
    }

    onPromptConfigChange({ ...promptConfig, [field]: event.target.value });
  }

  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const field = event.target.name;
    onPromptConfigChange({ ...promptConfig, [field]: checked });
  }

  return (
    <Dialog
      maxWidth="xl"
      fullWidth={true}
      PaperProps={{
        style: {
          backgroundColor: '#03bfcb',
        },
      }}
      open={open}
      onClose={() => onOpenChange(false)}
    >
      <DialogTitle>Prompt Config</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FormControlLabel className="spacer"
          control={
            <Switch
              checked={promptConfig.enable}
              onChange={handleSwitchChange}
              name="enable"
              color="primary"
            />
          }
          label="Enable Prompts"
        />
        <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.enableForFeed}
                  onChange={handleSwitchChange}
                  name="enableForFeed"
                  color="primary"
                />
              }
              label="Enable Feed Prompts"
            />
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.enableForReel}
                  onChange={handleSwitchChange}
                  name="enableForReel"
                  color="primary"
                />
              }
              label="Enable Reel Prompts"
            />
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.enableForStory}
                  onChange={handleSwitchChange}
                  name="enableForStory"
                  color="primary"
                />
              }
              label="Enable Story Prompts"
            />
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.enableForWatch}
                  onChange={handleSwitchChange}
                  name="enableForWatch"
                  color="primary"
                />
              }
              label="Enable Watch Prompts"
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.autoMove}
                  onChange={handleSwitchChange}
                  name="autoMove"
                  color="primary"
                />
              }
              label="Auto Move"
            />
            <TextField
              disabled={!promptConfig.enable}
              inputProps={{ min: 0, type: "number" }}
              variant="outlined"
              label="Auto Move Time"
              name="autoMoveTime"
              value={promptConfig.autoMoveTime}
              onChange={handlePromptConfigChange}
            />
            <FormControl disabled={!promptConfig.enable} className="spacer" variant="outlined">
              <InputLabel>Auto Move Type</InputLabel>
              <Select
                value={promptConfig.autoMoveType}
                onChange={handlePromptConfigChange}
                label="Auto Move Type"
                name="autoMoveType"
              >
                <MenuItem value={"page_start"}>Page Start</MenuItem>
                <MenuItem value={"content_start"}>Content Start</MenuItem>
                <MenuItem value={"ad_start"}>Ad Start</MenuItem>
                <MenuItem value={"ad_end"}>Ad End</MenuItem>
                <MenuItem value={"ad_seen"}>All Ads Seen</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.showNext}
                  onChange={handleSwitchChange}
                  name="showNext"
                  color="primary"
                />
              }
              label="Show Next"
            />
            <TextField
              disabled={!promptConfig.enable}
              inputProps={{ min: 0, type: "number" }}
              variant="outlined"
              label="Show Next Time"
              name="showNextTime"
              value={promptConfig.showNextTime}
              onChange={handlePromptConfigChange}
            />
            <FormControl disabled={!promptConfig.enable} className="spacer" variant="outlined">
              <InputLabel>Show Next Type</InputLabel>
              <Select
                value={promptConfig.showNextType}
                onChange={handlePromptConfigChange}
                label="Show Next Type"
                name="showNextType"
              >
                <MenuItem value={"page_start"}>Page Start</MenuItem>
                <MenuItem value={"content_start"}>Content Start</MenuItem>
                <MenuItem value={"ad_start"}>Ad Start</MenuItem>
                <MenuItem value={"ad_end"}>Ad End</MenuItem>
                <MenuItem value={"ad_seen"}>All Ads Seen</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.inactive}
                  onChange={handleSwitchChange}
                  name="inactive"
                  color="primary"
                />
              }
              label="Inactive"
            />
            <TextField
              disabled={!promptConfig.enable}
              inputProps={{ min: 0, type: "number" }}
              variant="outlined"
              label="Inactive Time"
              name="inactiveTime"
              value={promptConfig.inactiveTime}
              onChange={handlePromptConfigChange}
            />
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.inactiveRepeat}
                  onChange={handleSwitchChange}
                  name="inactiveRepeat"
                  color="primary"
                />
              }
              label="Inactive Repeat"
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.notSelecting}
                  onChange={handleSwitchChange}
                  name="notSelecting"
                  color="primary"
                />
              }
              label="Not Selecting"
            />
            <TextField
              disabled={!promptConfig.enable}
              inputProps={{ min: 0, type: "number" }}
              variant="outlined"
              label="Not Selecting Time"
              name="notSelectingTime"
              value={promptConfig.notSelectingTime}
              onChange={handlePromptConfigChange}
            />
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.notSelectingRepeat}
                  onChange={handleSwitchChange}
                  name="notSelectingRepeat"
                  color="primary"
                />
              }
              label="Not Selecting Repeat"
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <FormControlLabel className="spacer"
              disabled={!promptConfig.enable}
              control={
                <Switch
                  checked={promptConfig.fail}
                  onChange={handleSwitchChange}
                  name="fail"
                  color="primary"
                />
              }
              label="Fail"
            />
            <TextField
              disabled={!promptConfig.enable}
              inputProps={{ min: 0, type: "number" }}
              variant="outlined"
              label="Fail Time"
              name="failTime"
              value={promptConfig.failTime}
              onChange={handlePromptConfigChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={() => onOpenChange(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  )
};